import React, { useEffect } from "react";
import { detect } from "detect-browser";

import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useSnackbar } from "notistack";

import { IconButton, Link, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  customComponentsForReactMarkdown, // the object with all custom components
} from "utils/customComponentsForReactMarkdown";

/**
 * You should have something like this in your appConfig.json in
 * order for this to work:
 * 
 * "announcements": [
      {
        "id": 1, // Numeric. Must be a unique ID for this item in array of objects.
        "text": "Message to show to the user. _MarkDown_ and <i>HTML</i> are allowed.", // String. Self-documenting. 
        "active": true, // Boolean. Makes it possible to turn off messages completely without removing them.
        "showOnlyOnce": true, // Boolean. If true, a cookie will be saved on users browser and message will show only once.
        "maps": ["jw", "map_1"], // Array. Only specified maps will show message.
        "timeout": null, // null or Numeric. Snackbar will auto hide after specified amount of millisecond, or be persistent (if null).
        "startTime": "2020-01-01", // DateTime. Earliest timestamp for this to be visible. Must be a string parsable by Date.parse().
        "stopTime": "2020-12-31", // DateTime. Last timestamp when this will be visible.
        "browsers": ["ie"], // Array of browsers (see https://github.com/DamonOehlman/detect-browser for valid names)
        "type": "info" // String. See Notistack docs for allowed options, usually "default", "info", "warning", "success" and "error".
      }, {
        "id": 2, 
        // ... and so on
      }, 
      // ... and more objects here
    ]
 * 
 */

function Announcement({ announcements = [], currentMap }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Runs once. Filters the supplied announcements according to some conditions:
   *  - should this be shown for current map?
   *  - is it set to be activated?
   *  - does it fit within the (optional) date/time limitations?
   *  - is it set to only show up once, and if so, has it already been shown?
   *
   * All items that make it through all these tests are then sent to the
   * snackbar, which shows a nice message to the user.
   */
  useEffect(() => {
    // First, we define some helper filter functions.

    /**
     * Time filter. If an item has start/stop restrictions,
     * compare those to the current timestamp. Show only if the restrictions
     * are met.
     */
    const timeFilter = (a) => {
      const now = new Date().getTime();
      const startTime = Date.parse(a.startTime);
      const stopTime = Date.parse(a.stopTime);

      const validStartTime = !Number.isNaN(startTime);
      const validStopTime = !Number.isNaN(stopTime);

      if (validStartTime && validStopTime) {
        // If both start and stop are sat, now must be within
        return startTime < now && now < stopTime;
      } else if (validStartTime && !validStopTime) {
        // There's only a start time - show indefinitely if we're past it
        return startTime < now;
      } else if (!validStartTime && validStopTime) {
        // There's only a stop time, show if we haven't got there yet
        return now < stopTime;
      }

      // Finally, if none of the date strings parsed, it
      // means there are no time restrictions, let's show this.
      return true;
    };

    /**
     * See if ID of a given item exists in an array retrieved from local storage.
     * If the item is set to be shown only once, and it exists in the array already,
     * don't show it again. If it, on the other hand, is missing from the array,
     * show it _and_ add its ID to the array, so it won't show up again.
     */
    const localStorageFilter = (a) => {
      // If local storage flag is off, show this item.
      if (a.showOnlyOnce !== true) return true;

      // If we got this far, admin wants to use local storage.

      // First, see if we've already shown this item.
      const shownIds =
        window.localStorage.getItem("shownAnnouncementIds") || ""; // Avoid null value - it can't be parsed to array easily
      const aShownIds = shownIds.split(",").filter(Boolean); // Create an array by splitting on commas; remove empty values by comparing to a Boolean
      if (aShownIds.includes(a.id.toString())) {
        // If ID is in array, item has already been shown
        return false;
      }

      // If we got this far, admin want's to show this item,
      // but only once - so let's return true, but also add
      // current id to the cookie array.
      aShownIds.push(a.id);
      const sShownIds = aShownIds.join();
      window.localStorage.setItem("shownAnnouncementIds", sShownIds);
      return true;
    };

    /**
     * If "maps" property exists and current map exists in the "maps" array
     * or if "maps" is a string with value "all", show this notification.
     * Else, don't show it.
     */
    const mapFilter = (a) => {
      // If "all" is specified, no filtering is needed
      if (a?.maps === undefined || a.maps === "all") return true;

      // Else if an array is provided, check and see that current map exists in the array
      if (Array.isArray(a.maps)) return a.maps.includes(currentMap);

      // If anything else is provided, it is an invalid value. Just exit.
      return false;
    };

    /**
     * If "browsers" property exists and is an Array, return true only
     * if current browsers is included in the array.
     * If "browsers" is a string with value "all", or if it's undefined,
     * return true too.
     * For any other (invalid) value, return false.
     */
    const browserFilter = (a) => {
      const browsers = a?.browsers;
      if (browsers === undefined || browsers === "all") return true;
      if (Array.isArray(browsers)) return browsers.includes(detect().name);
      return false;
    };

    /**
     * Helper method: whatever is left in the announcements array after
     * all checks will be mapped to this render method.
     */
    const renderSnackbar = (f) => {
      if (typeof f.text !== "string") return; // A string is required. If there's nothing to display, get out of here.

      // Persistent snackbars will need an action that displays a close button.
      const action = (key) => {
        return (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => closeSnackbar(key)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        );
      };

      enqueueSnackbar(
        // This <div> is absolutely necessary because this component will render inside a Flexbox.
        // ReactMarkdown may produce multiple elements (e.g. <p>s), which would then end up next
        // to each other (because of the flexbox), unless they have a common parent element like this.
        <div>
          <ReactMarkdown
            children={f.text} // Our MD, as a text string
            rehypePlugins={[rehypeRaw]}
            components={{
              ...customComponentsForReactMarkdown,
              // Override P: we don't want an actual paragraph with margin-bottom
              // and all here. Just a plain MUI Typography will do.
              p: ({ children }) => {
                return !children ? null : (
                  <Typography variant="body2">{children}</Typography>
                );
              },
              // Override Link to force the same color as for the rest of the text. We
              // can't know for sure which Link color would work best here as it depends
              // on the variant of our current Snackbar. So let's just use text color.
              a: ({ children, href, title }) => {
                return children ? (
                  <Link
                    href={href}
                    title={title}
                    target="_blank"
                    color="inherit"
                    underline="always"
                  >
                    {children}
                  </Link>
                ) : null;
              },
            }}
          />
        </div>,
        {
          variant: f?.type || "default", // Allowed variants are "default", "info", "warning", "success" and "error"
          ...(Number.isFinite(f?.timeout) && { autoHideDuration: f?.timeout }), // If timeout is Numeric, auto hide
          ...(!Number.isFinite(f?.timeout) && { persist: true, action }), // If timeout isn't Numeric, snackbar is persistent
        }
      );
    };

    // Now we're done defining helpers and can proceed with the actual filtration.
    // Let's oop through all announcements and do some checks to filter out those
    // that should be displayed.
    const filtered = announcements
      .filter((a) => mapFilter(a)) // Show only announcements for the current map
      .filter((a) => a.active === true) // Only active announcements
      .filter((a) => browserFilter(a)) // Show only for some browsers if admin said so
      .filter((a) => timeFilter(a)) // Respect possible date/time restrictions
      .filter((a) => localStorageFilter(a)); // Show only once if admin said so, by checking a local storage setting

    // Filtering is done, now let's invoke render for those
    // items that made it this far.
    filtered.forEach((f) => renderSnackbar(f));
  }, [announcements, currentMap, enqueueSnackbar, closeSnackbar]);

  // Finally, React's render _must_ return something…
  return null;
}

// Prevent unnecessary re-runs by explicitly comparing the relevant prop
function arePropsEqual(prevProps, nextProps) {
  return prevProps.announcements.length === nextProps.announcements.length;
}

export default React.memo(Announcement, arePropsEqual);
